import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation } from "react-router-dom";
import {
  faCheckCircle,
  faEnvelope,
  faLayerGroup,
  faLock,
  faPen,
  faPhone,
  faPlus,
  faRotate,
  faSearch,
  faSpinner,
  faTimes,
  faTrash,
  faUserGroup,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../components/Ui_Element/CustomToast";
import config from "../../config";
import { deleteitem, getById, getlist, post } from "../../contexts/apicontexts";
import { useTheme } from "../../contexts/themContext";
import {
  getCurrentUserIdentity,
  getIdentityRoleLabel,
  isCustomerAdminIdentity,
  isStaffIdentity,
} from "../Tickets/ticketUtils";

const BRAND_LR = "linear-gradient(to right, #C04C2A, #4A1B13)";
const BRAND_135 = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const emptyForm = {
  id: 0,
  userId: "",
  fullName: "",
  email: "",
  mobile: "",
  empId: "",
  designation: "",
  department: "",
  password: "",
  isActive: true,
};

const normalizeStatus = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return true;
  return normalized === "active" || normalized === "1" || normalized === "true" || normalized === "enabled";
};

const normalizeCustomerUser = (item) => ({
  id: Number(item?.id ?? item?.Id ?? 0),
  userId: String(item?.userId ?? item?.UserId ?? item?.identityUserId ?? item?.IdentityUserId ?? ""),
  fullName: String(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name ?? ""),
  email: String(item?.email ?? item?.Email ?? ""),
  mobile: String(item?.mobile ?? item?.Mobile ?? item?.phoneNumber ?? item?.PhoneNumber ?? ""),
  empId: String(item?.empId ?? item?.EmpId ?? item?.employeeId ?? item?.EmployeeId ?? ""),
  designation: String(item?.designation ?? item?.Designation ?? ""),
  department: String(item?.department ?? item?.Department ?? ""),
  password: "",
  isActive: normalizeStatus(item?.isActive ?? item?.IsActive ?? item?.status ?? item?.Status),
  roleName: String(item?.roleName ?? item?.RoleName ?? "Customer User"),
  customerName: String(item?.customerName ?? item?.CustomerName ?? ""),
  customerCode: String(item?.customerCode ?? item?.CustomerCode ?? ""),
  organizationName: String(item?.organizationName ?? item?.OrganizationName ?? ""),
  lastLoginAt: item?.lastLoginAt ?? item?.LastLoginAt ?? null,
});

const formatDateTime = (value) => {
  if (!value) return "Not available";

  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const joinClasses = (...items) => items.filter(Boolean).join(" ");

const StatCard = ({ label, value, helper, icon, accent }) => (
  <div className="rounded-2xl border border-orange-100/80 bg-white/95 p-4 shadow-sm transition-colors duration-300 dark:border-slate-700 dark:bg-slate-900/85">
    <div className="mb-3 h-1 w-20 rounded-full" style={{ background: accent }} />
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-1 text-3xl font-semibold text-slate-900 dark:text-white">{value}</div>
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</div>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: accent }}>
        <FontAwesomeIcon icon={icon} />
      </div>
    </div>
  </div>
);

function CustomerUserField({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {error ? <div className="mt-2 text-xs font-medium text-rose-600">{error}</div> : null}
    </div>
  );
}

export default function CustomerUsersPage() {
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const { isDarkMode } = useTheme();
  const canManageUsers = useMemo(() => isCustomerAdminIdentity(identity) || isStaffIdentity(identity), [identity]);
  const identityRoleLabel = useMemo(() => getIdentityRoleLabel(identity), [identity]);
  const routedCustomerProfile = useMemo(
    () => location.state?.customerProfile || location.state?.customer || null,
    [location.state]
  );

  const [customerProfile, setCustomerProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return users;

    return users.filter((item) =>
      [item.fullName, item.email, item.mobile, item.empId, item.designation, item.department, item.roleName, item.customerName, item.organizationName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [searchTerm, users]);

  const allActiveUsers = useMemo(() => users.filter((item) => item.isActive), [users]);
  const activeLimit = Number(customerProfile?.maxActiveUsers ?? customerProfile?.MaxActiveUsers ?? 5) || 5;
  const seatsRemaining = Math.max(activeLimit - allActiveUsers.length, 0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        let profile = routedCustomerProfile;

        if (!profile) {
          const profileResponse = await getById(`${config.baseurl}Customer/dashboard`);
          profile = profileResponse?.status === 200 ? profileResponse.data : null;
        }

        if (cancelled) return;

        setCustomerProfile(profile);

        const customerId = profile?.customerId ?? profile?.CustomerId;
        const query = customerId ? `?customerId=${encodeURIComponent(customerId)}` : "";
        const userResponse = await getlist(`${config.baseurl}CustomerUser/getlist${query}`);

        if (cancelled) return;

        if (userResponse?.status === 200) {
          setUsers(Array.isArray(userResponse.data) ? userResponse.data.map(normalizeCustomerUser) : []);
        } else {
          setUsers([]);
          setError("Customer user service is not available yet. The page is ready, but the API endpoint still needs to return data.");
        }
      } catch {
        if (!cancelled) {
          setUsers([]);
          setError("Customer user records could not be loaded. If the backend API is still pending, this page will start working as soon as `CustomerUser/getlist` is available.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (!canManageUsers) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [canManageUsers, routedCustomerProfile]);

  const resetForm = () => {
    setFormState(emptyForm);
    setFormErrors({});
  };

  const openCreate = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEdit = (item) => {
    setFormState({
      id: item.id,
      userId: item.userId,
      fullName: item.fullName,
      email: item.email,
      mobile: item.mobile,
      empId: item.empId,
      designation: item.designation,
      department: item.department,
      password: "",
      isActive: item.isActive,
    });
    setFormErrors({});
    setIsOpen(true);
  };

  const validate = () => {
    const nextErrors = {};

    if (!formState.fullName.trim()) nextErrors.fullName = "Full name is required.";
    if (!formState.email.trim()) nextErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formState.email.trim())) nextErrors.email = "Email address is invalid.";
    if (!formState.id && !formState.password.trim()) nextErrors.password = "Password is required for a new customer user.";

    if (!formState.id && formState.isActive && allActiveUsers.length >= activeLimit) {
      nextErrors.isActive = `This customer account already uses all ${activeLimit} active seats.`;
    }

    if (formState.id) {
      const currentItem = users.find((item) => item.id === formState.id);
      const activatingFromInactive = currentItem && !currentItem.isActive && formState.isActive;

      if (activatingFromInactive && allActiveUsers.length >= activeLimit) {
        nextErrors.isActive = `No active seats are available. Increase the limit or deactivate another user first.`;
      }
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const refreshUsers = async () => {
    const customerId =
      customerProfile?.customerId ??
      customerProfile?.CustomerId ??
      routedCustomerProfile?.customerId ??
      routedCustomerProfile?.CustomerId ??
      routedCustomerProfile?.id ??
      routedCustomerProfile?.Id;
    const query = customerId ? `?customerId=${encodeURIComponent(customerId)}` : "";
    const response = await getlist(`${config.baseurl}CustomerUser/getlist${query}`);
    if (response?.status === 200) {
      setUsers(Array.isArray(response.data) ? response.data.map(normalizeCustomerUser) : []);
      return;
    }

    throw new Error("Refresh failed");
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setError("");

    const customerId = customerProfile?.customerId ?? customerProfile?.CustomerId ?? 0;
    const payload = {
      id: formState.id || 0,
      userId: formState.userId || "",
      customerId,
      fullName: formState.fullName.trim(),
      email: formState.email.trim(),
      mobile: formState.mobile.trim(),
      empId: formState.empId.trim(),
      designation: formState.designation.trim(),
      department: formState.department.trim(),
      password: formState.password.trim(),
      isActive: Boolean(formState.isActive),
      roleName: "Customer User",
    };

    try {
      const response = await post(`${config.baseurl}CustomerUser/${payload.id ? "update" : "add"}`, payload);
      if (response?.status === 200) {
        await refreshUsers();
        setIsOpen(false);
        resetForm();
        showToast.success(payload.id ? "Customer user updated successfully." : "Customer user added successfully.");
        return;
      }

      setError(response?.message || "Customer user could not be saved.");
    } catch {
      setError("Customer user could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.fullName || item.email}?`)) return;

    try {
      const response = await deleteitem(`${config.baseurl}CustomerUser/delete`, item.id);
      if (response?.status === 200) {
        await refreshUsers();
        showToast.success("Customer user deleted successfully.");
        return;
      }
      setError("Customer user could not be deleted.");
    } catch {
      setError("Customer user could not be deleted.");
    }
  };

  if (!canManageUsers) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-4 transition-colors duration-300 dark:from-slate-950 dark:via-[#0F111A] dark:to-slate-900 md:p-6">
        <Breadcrumb pageName="Customer Users" currentPage="Customer Users" />
        <div className="mt-4 rounded-3xl border border-amber-200 bg-amber-50 px-6 py-6 text-sm text-amber-900 shadow-sm transition-colors duration-300 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          Your current role is <span className="font-semibold">{identityRoleLabel}</span>. Customer user management is available for the main customer login or staff administrators.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-4 transition-colors duration-300 dark:from-slate-950 dark:via-[#0F111A] dark:to-slate-900 md:p-6">
      <Breadcrumb pageName="Customer Users" currentPage="Customer Users" />

      <div
        className="mt-4 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-xl transition-colors duration-300 dark:border-slate-800 dark:bg-[#161B22]"
        style={{ boxShadow: isDarkMode ? "0 18px 48px rgba(0,0,0,0.28)" : "0 18px 48px rgba(192,76,42,0.10)" }}
      >
        <div
          className="relative overflow-hidden px-5 py-6 text-white md:px-7 md:py-8"
          style={{ background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 55%, #020617 100%)" : BRAND_135 }}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_26%),repeating-linear-gradient(135deg,rgba(255,255,255,0.06)_0,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_18px)] opacity-80 dark:bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.16),transparent_28%),repeating-linear-gradient(135deg,rgba(148,163,184,0.08)_0,rgba(148,163,184,0.08)_2px,transparent_2px,transparent_18px)]" />
          <div className="absolute right-0 top-0 h-40 w-40 translate-x-8 -translate-y-10 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-50">
                <FontAwesomeIcon icon={faUsers} />
                Customer Access Control
              </div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Manage customer users</h1>

              <div className="mt-4 flex flex-wrap gap-3 text-xs text-orange-50/90 dark:text-slate-200">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{customerProfile?.name || customerProfile?.Name || "Customer account"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{customerProfile?.organizationName || customerProfile?.OrganizationName || "No organization"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Active seat limit: {activeLimit}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:bg-amber-300"
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Customer User
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoading(true);
                  refreshUsers()
                    .catch(() => setError("Customer user records could not be refreshed."))
                    .finally(() => setLoading(false));
                }}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 border-b border-orange-100 bg-gradient-to-r from-orange-50/60 to-red-50/30 p-5 transition-colors duration-300 dark:border-slate-800 dark:from-slate-900 dark:to-slate-900/80 md:grid-cols-4 md:p-6">
          <StatCard label="All Users" value={users.length} helper="Customer logins under this account" icon={faUserGroup} accent={BRAND_LR} />
          <StatCard label="Active" value={allActiveUsers.length} helper="Can sign in right now" icon={faCheckCircle} accent="linear-gradient(135deg, #059669 0%, #10b981 100%)" />
          <StatCard label="Inactive" value={Math.max(users.length - allActiveUsers.length, 0)} helper="Temporarily disabled access" icon={faLayerGroup} accent="linear-gradient(135deg, #f59e0b 0%, #f97316 100%)" />
          <StatCard label="Seats Left" value={seatsRemaining} helper={`Current limit ${activeLimit}`} icon={faUsers} accent="linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)" />
        </div>

        <div className="p-5 transition-colors duration-300 dark:bg-[#161B22] md:p-6">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Customer user directory</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Keep the extra customer logins active, inactive, and easy to audit.</p>
            </div>

            <div className="relative w-full max-w-md">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C04C2A]/55 dark:text-orange-300/70" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by name, email, employee ID, designation, or department"
                className="w-full rounded-2xl border border-orange-100 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
              />
            </div>
          </div>

          {error ? <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">{error}</div> : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/30 px-6 py-14 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading customer users...
            </div>
          ) : filteredUsers.length ? (
            <div className="space-y-3">
              {filteredUsers.map((item) => (
                <div
                  key={`${item.id}-${item.userId}`}
                  className="rounded-2xl border border-orange-100/80 bg-white/95 p-4 shadow-sm transition-colors duration-300 dark:border-slate-700 dark:bg-slate-900/85"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-orange-200/80 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8B2A15] dark:border-orange-400/20 dark:bg-orange-400/10 dark:text-orange-200">
                          {item.roleName}
                        </span>
                        <span
                          className={joinClasses(
                            "rounded-full px-2.5 py-1 text-xs font-semibold",
                            item.isActive
                              ? "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200"
                              : "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                          )}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                        {item.organizationName ? (
                          <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200">
                            {item.organizationName}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{item.fullName || "Unnamed User"}</h3>
                      <div className="mt-2 grid gap-2 text-sm text-slate-500 dark:text-slate-400 md:grid-cols-2">
                        <span className="inline-flex items-center gap-2">
                          <FontAwesomeIcon icon={faEnvelope} className="text-[#C04C2A] dark:text-orange-300" />
                          {item.email || "No email"}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <FontAwesomeIcon icon={faPhone} className="text-[#C04C2A] dark:text-orange-300" />
                          {item.mobile || "No mobile"}
                        </span>
                        <span>Customer: {item.customerName || customerProfile?.name || "Not linked"}</span>
                        <span>User ID: {item.userId || "Pending"}</span>
                        <span>Employee ID: {item.empId || "Not set"}</span>
                        <span>Designation: {item.designation || "Not set"}</span>
                        <span>Department: {item.department || "Not set"}</span>
                      </div>
                    </div>

                    <div className="shrink-0 text-right text-xs text-slate-500 dark:text-slate-400">
                      <div>Last login {formatDateTime(item.lastLoginAt)}</div>
                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-[#8B2A15] transition hover:bg-orange-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                        >
                          <FontAwesomeIcon icon={faPen} />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:bg-rose-500/20"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="rounded-2xl border border-dashed px-6 py-16 text-center transition-colors duration-300"
              style={{
                borderColor: isDarkMode ? "rgba(100,116,139,0.48)" : "rgba(192,76,42,0.22)",
                background: isDarkMode ? "rgba(15,23,42,0.45)" : "rgba(192,76,42,0.03)",
              }}
            >
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-md" style={{ background: BRAND_LR }}>
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No customer users found</h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Add the first customer user to let extra contacts log in under this customer account.
              </p>
              <button
                type="button"
                onClick={openCreate}
                className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow-sm"
                style={{ background: BRAND_LR }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Customer User
              </button>
            </div>
          )}
        </div>
      </div>

      {isOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
          <div className="relative z-10 w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl transition-colors duration-300 dark:bg-[#161B22]">
            <div
              className="relative overflow-hidden border-b border-orange-100 px-6 py-5 text-white dark:border-slate-700"
              style={{ background: isDarkMode ? "linear-gradient(135deg, #1e293b 0%, #0f172a 58%, #020617 100%)" : BRAND_135 }}
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_26%),repeating-linear-gradient(135deg,rgba(255,255,255,0.06)_0,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_18px)] opacity-75 dark:bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.16),transparent_28%),repeating-linear-gradient(135deg,rgba(148,163,184,0.08)_0,rgba(148,163,184,0.08)_2px,transparent_2px,transparent_18px)]" />
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="relative pr-12">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-50/80 dark:text-slate-300">Customer User</p>
                <h3 className="mt-2 text-2xl font-semibold">{formState.id ? "Update customer user" : "Add a new customer user"}</h3>
                <p className="mt-2 text-sm text-orange-50/85 dark:text-slate-300">Every added user will use the dedicated role name: Customer User.</p>
              </div>
            </div>

            <div className="grid gap-5 p-6 transition-colors duration-300 dark:bg-[#161B22] md:grid-cols-2">
              <CustomerUserField label="Full Name" required error={formErrors.fullName}>
                <input
                  value={formState.fullName}
                  onChange={(event) => setFormState((current) => ({ ...current, fullName: event.target.value }))}
                  className="w-full rounded-2xl border border-orange-100 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                  placeholder="Example: Accounts Supervisor"
                />
              </CustomerUserField>

              <CustomerUserField label="Email" required error={formErrors.email}>
                <input
                  value={formState.email}
                  onChange={(event) => setFormState((current) => ({ ...current, email: event.target.value }))}
                  className="w-full rounded-2xl border border-orange-100 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                  placeholder="customer.user@example.com"
                />
              </CustomerUserField>

              <CustomerUserField label="Mobile">
                <input
                  value={formState.mobile}
                  onChange={(event) => setFormState((current) => ({ ...current, mobile: event.target.value }))}
                  className="w-full rounded-2xl border border-orange-100 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                  placeholder="+8801XXXXXXXXX"
                />
              </CustomerUserField>

              <CustomerUserField label="Employee ID">
                <input
                  value={formState.empId}
                  onChange={(event) => setFormState((current) => ({ ...current, empId: event.target.value }))}
                  className="w-full rounded-2xl border border-orange-100 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                  placeholder="Example: EMP-10025"
                />
              </CustomerUserField>

              <CustomerUserField label="Designation">
                <input
                  value={formState.designation}
                  onChange={(event) => setFormState((current) => ({ ...current, designation: event.target.value }))}
                  className="w-full rounded-2xl border border-orange-100 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                  placeholder="Example: Senior Accounts Executive"
                />
              </CustomerUserField>

              <CustomerUserField label="Department">
                <input
                  value={formState.department}
                  onChange={(event) => setFormState((current) => ({ ...current, department: event.target.value }))}
                  className="w-full rounded-2xl border border-orange-100 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                  placeholder="Example: Finance"
                />
              </CustomerUserField>

              <CustomerUserField label={formState.id ? "Reset Password" : "Password"} required={!formState.id} error={formErrors.password}>
                <div className="relative">
                  <FontAwesomeIcon icon={faLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C04C2A]/55 dark:text-orange-300/70" />
                  <input
                    type="password"
                    value={formState.password}
                    onChange={(event) => setFormState((current) => ({ ...current, password: event.target.value }))}
                    className="w-full rounded-2xl border border-orange-100 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-[#C04C2A] focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-orange-300 dark:focus:ring-orange-400/10"
                    placeholder={formState.id ? "Leave empty to keep current password" : "Temporary password"}
                  />
                </div>
              </CustomerUserField>

              <div className="md:col-span-2">
                <div className="rounded-2xl border border-orange-100 bg-orange-50/65 px-4 py-4 transition-colors duration-300 dark:border-slate-700 dark:bg-slate-900/75">
                  <label className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-white">Active login access</div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">Inactive users stay visible in the customer account, but they cannot use their login.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={formState.isActive}
                      onChange={(event) => setFormState((current) => ({ ...current, isActive: event.target.checked }))}
                      className="h-5 w-5 rounded border-orange-200 text-[#8B2A15] focus:ring-orange-200 dark:border-slate-600 dark:bg-slate-800 dark:text-orange-300 dark:focus:ring-orange-400/20"
                    />
                  </label>
                  {formErrors.isActive ? <div className="mt-3 text-xs font-medium text-rose-600">{formErrors.isActive}</div> : null}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-orange-100 bg-gradient-to-r from-orange-50/65 to-red-50/35 px-6 py-5 transition-colors duration-300 dark:border-slate-700 dark:from-slate-900 dark:to-slate-900/85 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-full border border-orange-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-orange-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-70"
                style={{ background: BRAND_LR }}
              >
                <FontAwesomeIcon icon={saving ? faSpinner : faPlus} spin={saving} />
                {saving ? "Saving..." : formState.id ? "Update Customer User" : "Save Customer User"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
